import { useEffect, useRef, useState } from "react";

import { FetchGet } from "@/lib/fetch-helper";
import { type AvailabilityState } from "@/src/cashback/CashbackCommonInterfaces";
import TransactionItem from "@/src/cashback/MainPage/Transactions/TransactionItem";
import TransactionItemMobile from "@/src/cashback/MainPage/Transactions/TransactionItemMobile";
import type { ITransactionItem } from "@/src/cashback/MainPage/Transactions/shared";
import { useInfiniteQueryCustom } from "@/src/query-client-overrides";
import { Loading } from "@Components/Loading";
import * as stylex from "@stylexjs/stylex";

import * as css from "./ScrollPagination.module.css";

export interface IScrollPaginationResult<T extends object> {
    data: T[];
    hasMore: boolean;
    emptyList: boolean;
}

export interface IScrollPagination {
    transactionStatus: AvailabilityState;
    pageSize: number;
    noScrollLoading?: boolean;
    headers?: JSX.Element;
    emptyListElement?: JSX.Element;
    isMobile?: boolean;
    isTemporaryPage?: boolean; // To remove after DPPRD-44182
}

const fetchTrxData = (transactionStatus: AvailabilityState, pageIndex: number, pageSize: number) => {
    return FetchGet<IScrollPaginationResult<ITransactionItem>>(
        `/api/cashback/Transactions?status=${transactionStatus}&pageIndex=${pageIndex}&pageSize=${pageSize}`,
    ).catch(() => {
        return {
            data: [] as ITransactionItem[],
            hasMore: false,
            emptyList: false,
        } satisfies IScrollPaginationResult<ITransactionItem>;
    });
};

const ScrollPagination = (props: IScrollPagination) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);

    const {
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage: isLoading,
        data,
    } = useInfiniteQueryCustom({
        queryKey: ["transactions", props.transactionStatus, props.pageSize],
        queryFn: (queryProps) => {
            const { pageParam, queryKey } = queryProps;
            const [, trxStatus, listPageSize] = queryKey;
            return fetchTrxData(trxStatus as AvailabilityState, pageParam, listPageSize as number);
        },
        initialPageParam: 0,

        getNextPageParam: (lastPage, allPages) => {
            if (lastPage?.hasMore === true) {
                return allPages.length;
            }

            return undefined; // this sets 'hasNextPage' to false
        },
        retry: 3,
    });

    useEffect(() => {
        const element = containerRef.current;
        if (!element || isLoading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setInView(entry.isIntersecting);
                });
            },
            {
                root: rootRef.current,
                threshold: 0,
            },
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [data, isLoading]);

    const itemsList = data?.pages?.flatMap((pagesArr) => pagesArr.data) || [];

    useEffect(() => {
        if (!inView || isLoading || !hasNextPage || props.noScrollLoading) {
            return;
        }

        setInView(false);
        fetchNextPage();
    }, [fetchNextPage, hasNextPage, inView, isLoading, props.noScrollLoading, itemsList.length]);

    const emptyListElement = () => {
        if (isLoading || !props.emptyListElement) {
            return null;
        }
        return props.emptyListElement;
    };

    const contextList = () => {
        if (itemsList.length == 0) {
            return emptyListElement();
        }

        const Comp = props.isMobile ? TransactionItemMobile : TransactionItem;
        return (
            <>
                {props.headers}
                <div className={css.scrollContent} ref={rootRef}>
                    {itemsList.map((item, idx) => (
                        <Comp
                            {...item}
                            key={item.id}
                            isExtended={props.isTemporaryPage && !idx}
                            isTemporaryPage={props.isTemporaryPage}
                        />
                    ))}
                    {!props.noScrollLoading && (
                        <div
                            {...stylex.props(styles.loadingLayer, !hasNextPage && styles.hidden)}
                            ref={containerRef}
                        >
                            <Loading noModalLayer />
                        </div>
                    )}
                </div>
            </>
        );
    };

    return (
        <div className={css.scrollPaginationComponent}>
            {data === undefined ? <Loading /> : contextList()}
        </div>
    );
};

export default ScrollPagination;

const styles = stylex.create({
    loadingLayer: {
        padding: "16px",
    },
    hidden: {
        display: "none",
    },
});
